import { alpha, createTheme, darken, Theme } from '@mui/material';
// Registers MUI X picker component keys (MuiPickersInputBase, etc.) on the
// MUI theme's `components` type so they can be styled via styleOverrides.
import '@mui/x-date-pickers/themeAugmentation';

export interface ThemeJson {
  colors: {
    primary: string;
    secondary: string;
    success: string;
    warning: string;
    error: string;
    info: string;
    black: string;
    white: string;
    primaryAlt?: string;
  };
  gradients: {
    blue1: string;
    blue2: string;
    blue3: string;
    blue4: string;
    blue5: string;
    orange1: string;
    orange2: string;
    orange3: string;
    purple1: string;
    purple2: string;
    purple3: string;
    pink1: string;
    pink2: string;
    green1: string;
    green2: string;
    black1: string;
    black2: string;
    lightblue1: string;
  };
  typography: {
    fontFamily: string;
  };
  static: {
    borderRadius: number;
    boxShadow: string;
    inputHeight: number;
    bodyBg: string;
    dividerBg: string;
    menuItemColor: string;
    menuItemBgActive: string;
  };
}
export function themeFromConfig(json: ThemeJson): Theme {
  const { colors: c, gradients: g, typography: t, static: s } = json;

  // Derive basics from the simpler JSON
  const background = s.bodyBg;
  const surface = c.white;
  const textPrimary = c.black;
  const textSecondary = alpha(c.black, 0.8);
  const textDisabled = alpha(c.black, 0.5);

  const primaryMain = c.primary;
  const primaryLight = alpha(c.primary, 0.3);
  const primaryDark = darken(c.primary, 0.2);

  const secondaryMain = c.secondary;
  const secondaryLight = alpha(c.secondary, 0.2);
  const secondaryDark = darken(c.secondary, 0.2);

  const errorMain = c.error;
  const errorLight = alpha(c.error, 0.3);

  const theme = createTheme({
    palette: {
      mode: 'light',
      primary: {
        main: primaryMain,
        light: primaryLight,
        dark: primaryDark,
        contrastText: c.white,
      },
      secondary: {
        main: secondaryMain,
        light: secondaryLight,
        dark: secondaryDark,
      },
      success: { main: c.success },
      error: { main: c.error },
      warning: { main: c.warning },
      info: { main: c.info },
      background: { default: background, paper: surface },
      text: {
        primary: textPrimary,
        secondary: textSecondary,
        disabled: textDisabled,
      },
      common: { white: c.white, black: c.black },
      action: {
        active: c.black,
        hover: primaryLight,
        selected: alpha(c.black, 0.1),
        disabled: alpha(c.black, 0.5),
        disabledBackground: alpha(c.black, 0.5),
        focus: alpha(c.black, 0.1),
        hoverOpacity: 0.1,
        focusOpacity: 0.05,
        activatedOpacity: 0.12,
        disabledOpacity: 0.38,
        selectedOpacity: 0.1,
      },
    },

    shape: { borderRadius: s.borderRadius },

    typography: {
      fontFamily: t.fontFamily,
      h1: {
        fontWeight: 700,
        fontSize: 35,
      },
      h2: {
        fontWeight: 700,
        fontSize: 30,
      },
      h3: {
        fontWeight: 700,
        fontSize: 25,
        lineHeight: 1.4,
        color: c.black,
      },
      h4: {
        fontWeight: 700,
        fontSize: 16,
      },
      h5: {
        fontWeight: 700,
        fontSize: 14,
      },
      h6: {
        fontSize: 15,
      },
      body1: {
        fontSize: 14,
      },
      body2: {
        fontSize: 14,
      },
      button: {
        fontWeight: 600,
      },
      caption: {
        fontSize: 13,
        textTransform: 'uppercase',
        color: alpha(c.black, 0.5),
      },
      subtitle1: {
        fontSize: 14,
        color: alpha(c.black, 0.7),
      },
      subtitle2: {
        fontWeight: 400,
        fontSize: 15,
        color: alpha(c.black, 0.7),
      },
      overline: {
        fontSize: 13,
        fontWeight: 700,
        textTransform: 'uppercase',
      },
      regCaption: {
        fontSize: 12,
        fontWeight: 400,
        color: alpha(c.black, 0.6),
        fontFamily: 'Inter',
      },
      regBody: {
        fontWeight: 400,
        fontFamily: 'Inter',
        fontSize: 14,
        color: alpha(c.black, 0.8),
      },
      medBody: {
        fontFamily: 'Inter-Medium',
        fontSize: 14,
        color: alpha(c.black, 0.8),
        fontWeight: 500,
      },
      medTitle: {
        fontFamily: 'Inter-Medium',
        fontWeight: 500,
        fontSize: 16,
        color: alpha(c.black, 0.8),
      },
      medCaption: {
        fontFamily: 'Inter-Medium',
        fontWeight: 500,
        fontSize: 12,
        color: alpha(c.black, 0.8),
      },
    },

    components: {
      MuiCssBaseline: {
        styleOverrides: {
          'html, body': {
            width: '100%',
            height: '100%',
          },
          body: {
            background,
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100%',
            width: '100%',
            flex: 1,
          },
          '#root': {
            width: '100%',
            height: '100%',
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
          },
          html: {
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100%',
            width: '100%',
            MozOsxFontSmoothing: 'grayscale',
            WebkitFontSmoothing: 'antialiased',
          },
          '.child-popover .MuiPaper-root .MuiList-root': {
            flexDirection: 'column',
          },
          '#nprogress': {
            pointerEvents: 'none',
          },
          '#nprogress .bar': {
            backgroundColor: primaryLight,
          },
          '#nprogress .spinner-icon': {
            borderTopColor: primaryLight,
            borderLeftColor: primaryLight,
          },
          '#nprogress .peg': {
            boxShadow: '0 0 15px ' + primaryLight + ', 0 0 8px' + primaryLight,
          },
          ':root': {
            '--swiper-theme-color': primaryMain,
          },
          code: {
            background: c.info,
            color: c.info,
            borderRadius: 4,
            padding: 4,
          },
          '@keyframes pulse': {
            '0%': {
              transform: 'scale(.75)',
            },
            '20%': {
              transform: 'scale(1.1)',
            },
            '40%': {
              transform: 'scale(.75)',
            },
            '60%': {
              transform: 'scale(1.05)',
            },
            '80%': {
              transform: 'scale(.75)',
            },
            '100%': {
              transform: 'scale(.75)',
            },
          },
          '@keyframes ripple': {
            '0%': {
              transform: 'scale(.8)',
              opacity: 1,
            },
            '100%': {
              transform: 'scale(2.8)',
              opacity: 0,
            },
          },
          '@keyframes float': {
            '0%': {
              transform: 'translate(0%, 0%)',
            },
            '100%': {
              transform: 'translate(3%, 3%)',
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            borderRadius: '4px',
            '& .MuiFormLabel-root': {
              color: alpha(c.secondary, 0.7), // unfocused label color
            },
            '& .MuiFormLabel-root.Mui-focused': {
              color: c.secondary, // focused label color
            },

            // --- INPUT BASE (applies to all variants) ---
            '& .MuiInputBase-root': {
              color: '#000',
              height: '40px',
              minHeight: '40px',
              background: surface,
              '& input': {
                padding: '8px 12px',
                // height: '40px',
              },
            },
            '& .MuiInputBase-multiline': {
              height: 'auto',
              minHeight: '40px !important',

              '& textarea': {
                minHeight: '40px !important',
                boxSizing: 'border-box',
              },
            },
            '& .MuiInputBase-root.Mui-disabled': {
              backgroundColor: '#f5f5f5',
              color: '#999',
            },
            // Autofill background override
            '& input:-webkit-autofill': {
              WebkitBoxShadow: '0 0 0px 1000px white inset !important',
              WebkitTextFillColor: '#000', // keep text readable
            },
            '& input:-webkit-autofill:focus': {
              boxShadow: '0 0 0px 1000px white inset !important',
            },
            '& input:-webkit-autofill:hover': {
              boxShadow: '0 0 0px 1000px white inset !important',
            },
            // --- OUTLINED INPUT ---
            '& .MuiOutlinedInput-root': {
              borderRadius: '4px',
              background: 'white',
              '& fieldset': {
                background: 'transparent',
                color: '#999',
                borderColor: alpha(c.primary, 0.2),
              },
              '&:hover fieldset': {
                borderColor: alpha(c.primary, 0.2),
              },
              '&.Mui-focused fieldset': {
                borderColor: alpha(c.primary, 0.2),
                borderWidth: 1.5,
              },
              '&.Mui-disabled fieldset': {
                borderColor: alpha(c.primary, 0.2),
              },
              '&.Mui-error fieldset': {
                borderColor: errorMain,
              },
            },
            // --- ERROR STATES ---
            // Only the label and helper text turn red; the typed value keeps
            // the input's own colour so a search inside an errored field
            // stays readable.
            '& .MuiFormLabel-root.Mui-error, & .MuiFormHelperText-root.Mui-error':
              {
                color: errorMain,
              },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: { root: { marginLeft: 0, textTransform: 'none' } },
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            minWidth: 113,
            borderRadius: '4px',
            paddingBlock: '4px',
            paddingLeft: '16px',
            paddingRight: '12px',
            '&:hover': {
              borderColor: alpha(c.black, 0.5),
            },
            height: 32,
          },
          icon: {
            color: alpha(c.black, 0.5),
            right: 12,
            '&:hover': {
              color: primaryMain,
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            paddingRight: 14,

            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: alpha(c.primary, 0.2),
            },

            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: primaryMain,
            },

            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: primaryMain,
            },
            '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
              borderColor: 'transparent',
            },
          },
        },
      },
      // MUI X v8 pickers (DatePicker/TimePicker) render PickersTextField/
      // PickersOutlinedInput instead of the standard TextField/OutlinedInput,
      // so the MuiTextField/MuiOutlinedInput overrides above don't reach them.
      // Mirror the input sizing/border styling here so pickers match app-wide.
      MuiPickersInputBase: {
        styleOverrides: {
          root: {
            height: '40px',
            minHeight: '40px',
            background: surface,
            borderRadius: '4px',
            color: '#000',
          },
        },
      },
      MuiPickersOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: '4px',
            background: surface,
            '& .MuiPickersOutlinedInput-notchedOutline': {
              borderColor: alpha(c.primary, 0.2),
            },
            '&:hover .MuiPickersOutlinedInput-notchedOutline': {
              borderColor: primaryMain,
            },
            '&.Mui-focused .MuiPickersOutlinedInput-notchedOutline': {
              borderColor: primaryMain,
            },
            '&.Mui-disabled .MuiPickersOutlinedInput-notchedOutline': {
              borderColor: 'transparent',
            },
          },
        },
      },
      MuiInput: {
        styleOverrides: {
          root: {
            '&:before': {
              borderColor: alpha(c.primary, 0.2),
            },
            '&:hover:not(.Mui-disabled):before': {
              borderColor: alpha(c.primary, 0.2) + ' !important',
            },
            '&:after': {
              borderColor: primaryMain,
            },
          },
        },
      },

      MuiListSubheader: {
        styleOverrides: {
          colorPrimary: {
            fontWeight: 'bold',
            lineHeight: '40px',
            fontSize: 13,
            background: alpha(c.black, 0.5),
            color: alpha(c.black, 0.7),
          },
        },
      },
      MuiCardHeader: {
        styleOverrides: {
          action: {
            marginTop: -5,
            marginBottom: -5,
          },
          title: {
            fontSize: 15,
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            '&.Mui-checked': {
              color: secondaryMain,
            },
            // Remove the global ButtonBase disabled background so the
            // checkbox doesn't look highlighted when disabled
            '&.Mui-disabled': {
              backgroundColor: 'transparent !important',
              color: alpha(c.black, 0.26),
              '&.Mui-checked': {
                color: alpha(secondaryMain, 0.4),
              },
            },
          },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            borderRadius: '50px',
            color: alpha(c.black, 0.4),
            '&.Mui-checked': {
              color: secondaryMain,
            },
            // Remove the global ButtonBase disabled background so the
            // radio doesn't look highlighted when disabled
            '&.Mui-disabled': {
              backgroundColor: 'transparent !important',
              color: alpha(c.black, 0.26),
              '&.Mui-checked': {
                color: alpha(secondaryMain, 0.4),
              },
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: '6px',
            padding: '4px 12px',
          },
          colorSuccess: {
            background: alpha(c.success, 0.2),
            color: c.success,

            '&:hover': {
              background: alpha(c.success, 0.2),
            },
          },
          colorInfo: {
            background: '#F1F6FE',
            color: '#403CAB',

            '&:hover': {
              background: alpha('#403CAB', 0.1),
            },
          },
          colorError: {
            background: alpha(c.error, 0.1),
            color: errorMain,

            '&:hover': {
              background: alpha(c.error, 0.1),
            },
          },
          colorSecondary: {
            background: alpha(c.secondary, 0.1),
            color: secondaryMain,

            '&:hover': {
              background: alpha(c.secondary, 0.1),
            },
          },
          colorWarning: {
            background: '#FDF6EC',
            color: c.warning,

            '&:hover': {
              background: '#FDF6EC',
            },
          },
          colorPrimary: {
            background: alpha(c.primary, 0.1),
            color: primaryMain,
            '&:hover': {
              background: alpha(c.primary, 0.1),
            },
          },
          deleteIcon: {
            color: errorLight,

            '&:hover': {
              color: errorMain,
            },
          },
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            boxShadow: 'none',

            '&.Mui-expanded': {
              margin: 0,
            },
            '&::before': {
              display: 'none',
            },
          },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            fontSize: 14,
            fontWeight: 'bold',
          },
          colorDefault: {
            background: alpha(c.black, 0.3),
            color: c.white,
          },
        },
      },
      MuiAvatarGroup: {
        styleOverrides: {
          root: {
            alignItems: 'center',
          },
          avatar: {
            background: alpha(c.black, 0.1),
            fontSize: 13,
            color: alpha(c.black, 0.7),
            fontWeight: 'bold',

            '&:first-of-type': {
              border: 0,
              background: 'transparent',
            },
          },
        },
      },
      MuiListItemAvatar: {
        styleOverrides: {
          alignItemsFlexStart: {
            marginTop: 0,
          },
        },
      },
      MuiPaginationItem: {
        styleOverrides: {
          page: {
            fontSize: 12,
            fontWeight: 400,
            transition: 'all .2s',
          },
          textPrimary: {
            '&.Mui-selected': {
              boxShadow:
                '0px 1px 4px rgba(28, 52, 140, 0.25), 0px 3px 12px 2px rgba(28, 52, 140, 0.35)',
            },
            '&.MuiButtonBase-root:hover': {
              background: alpha(c.black, 0.5),
            },
            '&.Mui-selected.MuiButtonBase-root:hover': {
              background: primaryMain,
            },
          },
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          root: {
            fontFamily: 'Inter',
            color: alpha(c.black, 0.6),
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableRipple: true,
        },
        styleOverrides: {
          root: {
            borderRadius: '4px',
            fontWeight: 400,
            textTransform: 'none',
            height: '40px',

            '.MuiSvgIcon-root': {
              transition: 'all .2s',
            },
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none',
            },
            '&.Mui-disabled': {
              backgroundColor: `${alpha(c.primary, 0.1)} !important`,
              color: alpha(c.black, 0.4),
            },
          },
          endIcon: {
            marginRight: -8,
          },
          contained: {
            '&:disabled': {
              backgroundColor: alpha(c.black, 0.1),
            },
          },
          containedError: {
            backgroundColor: '#FCEBED',
            color: '#BB2735',
          },
          containedSecondary: {
            backgroundColor: secondaryMain,
            color: c.white,
            border: '1px solid ' + alpha(c.black, 0.3),
          },
          outlined: {
            color: alpha(c.black, 0.8),
            border: 'none',
            outline: '1px solid #00000014',
            background: '#FFFFFF;',
            '&:hover, &.MuiSelected': {
              backgroundColor: alpha(c.black, 0.1),
              color: c.black,
            },
          },
          outlinedSecondary: {
            backgroundColor: c.white,
            '&:hover, &.MuiSelected': {
              backgroundColor: alpha(c.black, 0.5),
              color: c.black,
            },
          },
          sizeSmall: {
            padding: '6px 16px',
            lineHeight: 1.5,
          },
          sizeMedium: {
            padding: '10px 24px',
          },
          sizeLarge: {
            padding: '11px 24px',
          },
          textSizeSmall: {
            padding: '7px 12px',
          },
          textSizeMedium: {
            padding: '9px 16px',
          },
          textSizeLarge: {
            padding: '12px 16px',
          },
        },
      },
      MuiButtonBase: {
        defaultProps: {
          disableRipple: false,
        },
        styleOverrides: {
          root: {
            borderRadius: 0,
            '&.Mui-disabled': {
              backgroundColor: `${alpha(c.primary, 0.1)} !important`,
              color: alpha(c.black, 0.4),
            },
          },
        },
      },
      MuiToggleButton: {
        defaultProps: {
          disableRipple: true,
        },
        styleOverrides: {
          root: {
            color: primaryMain,
            background: c.white,
            transition: 'all .2s',

            '&:hover, &.Mui-selected, &.Mui-selected:hover': {
              color: c.white,
              background: primaryMain,
            },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            padding: 8,

            '& .MuiTouchRipple-root': {
              borderRadius: 8,
            },

            // Show boxShadow on hover
            // '&:hover': {
            //   backgroundColor: 'transparent',
            //   boxShadow: `0 0 0 3px ${alpha(primaryMain, 0.05)}`,
            // },

            // Customize focus-visible style: keep transparent background but add a visible focus ring
            '&.Mui-focusVisible': {
              backgroundColor: 'transparent',
              boxShadow: `0 0 0 3px ${alpha(primaryMain, 0.2)}`,
            },

            // Remove disabled background ONLY for IconButton
            '&.Mui-disabled': {
              backgroundColor: 'transparent !important',
            },
          },

          sizeSmall: {
            padding: 4,
          },
        },
      },
      MuiListItemText: {
        styleOverrides: {
          root: {
            margin: 0,
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            '& .MuiTouchRipple-root': {
              opacity: 0.3,
            },
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            background: alpha(c.black, 0.1),
            border: 0,
            height: 1,
          },
          vertical: {
            height: 'auto',
            width: 1,

            '&.MuiDivider-flexItem.MuiDivider-fullWidth': {
              height: 'auto',
            },
            '&.MuiDivider-absolute.MuiDivider-fullWidth': {
              height: '100%',
            },
          },
          withChildren: {
            '&:before, &:after': {
              border: 0,
            },
          },
          wrapper: {
            background: c.white,
            fontWeight: 'bold',
            height: 24,
            lineHeight: '24px',
            marginTop: -12,
            color: 'inherit',
            textTransform: 'uppercase',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            padding: 0,
          },
          elevation0: {
            boxShadow: 'none',
          },
          elevation: {
            boxShadow:
              '0px 9px 16px rgba(159, 162, 191, .18), 0px 2px 2px rgba(159, 162, 191, 0.32)',
          },
          elevation2: {
            boxShadow:
              '0px 2px 3px rgba(159, 162, 191, .18), 0px 1px 1px rgba(159, 162, 191, 0.32)',
          },
          elevation24: {
            boxShadow:
              '0 5rem 14rem 0 rgb(255 255 255 / 30%), 0 0.8rem 2.3rem rgb(0 0 0 / 60%), 0 0.2rem 0.3rem rgb(0 0 0 / 45%)',
          },
          outlined: {
            boxShadow:
              '0px 9px 16px rgba(159, 162, 191, .18), 0px 2px 2px rgba(159, 162, 191, 0.32)',
          },
        },
      },
      MuiLink: {
        defaultProps: {
          underline: 'hover',
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            height: 6,
          },
        },
      },
      MuiSlider: {
        styleOverrides: {
          root: {
            '& .MuiSlider-valueLabelCircle, .MuiSlider-valueLabelLabel': {
              transform: 'none',
            },
            '& .MuiSlider-valueLabel': {
              borderRadius: 6,
              background: c.black,
              color: c.white,
            },
          },
        },
      },
      MuiList: {
        styleOverrides: {
          root: {
            padding: 0,

            '& .MuiListItem-button': {
              transition: 'all .2s',

              '& > .MuiSvgIcon-root': {
                minWidth: 34,
              },

              '& .MuiTouchRipple-root': {
                opacity: 0.2,
              },
            },
            '& .MuiListItem-root.MuiButtonBase-root.Mui-selected': {
              backgroundColor: primaryLight,
            },
            '& .MuiMenuItem-root.MuiButtonBase-root:active': {
              backgroundColor: primaryLight,
            },
            '& .MuiMenuItem-root.MuiButtonBase-root .MuiTouchRipple-root': {
              opacity: 0.2,
            },
          },
          padding: {
            padding: '12px',

            '& .MuiListItem-button': {
              borderRadius: 6,
              margin: '1px 0',
            },
          },
        },
      },
      MuiTabs: {
        // Tab bars scroll instead of clipping labels on narrow screens. Scroll
        // buttons only render when the tabs overflow, so a bar that fits looks
        // exactly as before; an explicit `variant` (e.g. fullWidth) still wins.
        defaultProps: {
          variant: 'scrollable',
          scrollButtons: 'auto',
          allowScrollButtonsMobile: true,
        },
        styleOverrides: {
          root: {
            height: 38,
            minHeight: 38,
            gap: '12px',
            alignItems: 'center',
          },
          indicator: {
            visibility: 'hidden',
          },
          scrollableX: {
            overflow: 'auto !important',
          },
          scrollButtons: {
            width: 20,
            height: 20,
            background: '#00000014',
            borderRadius: 15,
            color: c.white,
            '&.Mui-disabled': {
              opacity: 1,
              cursor: 'not-allowed', // optional
            },
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            marginRight: '12px',
            padding: 0,
            height: 38,
            minHeight: 38,
            borderRadius: '4px',
            transition: 'none',
            textTransform: 'capitalize',
            borderBottom: `1px solid ${primaryMain}`,
            color: '#00000099',
            fontWeight: 400,
            background: alpha(primaryMain, 0.05),
            '&.MuiButtonBase-root': {
              minWidth: 'auto',
              paddingLeft: 20,
              paddingRight: 20,
              marginRight: 12,
            },
            '&.Mui-selected, &.Mui-selected:hover': {
              color: secondaryMain,
              // background: alpha(secondaryMain, 0.2),
              background: secondaryLight,
              zIndex: 5,
              fontWeight: 500,
              borderBottom: `1px solid ${secondaryMain}`,
            },
            '&:active, :hover': {
              color: secondaryMain,
              background: secondaryLight,
              borderBottom: `1px solid ${secondaryMain}`,
              zIndex: 5,
            },
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            minWidth: 160,
            borderRadius: '4px',
            padding: 0,
          },
          list: {
            padding: '4px 0',

            '& .MuiMenuItem-root.MuiButtonBase-root': {
              fontSize: 14,
              marginBottom: 10,
              transition: 'all .2s',
              color: alpha(c.black, 0.7),

              '& .MuiTouchRipple-root': {
                opacity: 0.2,
              },

              '&:hover, &:active, &.active, &.Mui-selected': {
                color: c.black,
                background: primaryLight,
              },
              '&:last-child': {
                marginBottom: 0,
              },
            },
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            transition: 'all .2s',
            '&:hover, &:active, &.active, &.Mui-selected': {
              color: c.black,
              background: primaryLight,
            },
            '&.Mui-selected:hover': {
              background: primaryLight,
            },
          },
        },
      },
      MuiListItem: {
        styleOverrides: {
          root: {
            '&.MuiButtonBase-root': {
              color: secondaryMain,

              '&:hover, &:active, &.active, &.Mui-selected': {
                color: c.black,
                background: primaryLight,
              },
            },
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          root: ({ theme }) => ({
            // 🔹 Main input container
            position: 'relative',
            '& .MuiOutlinedInput-root': {
              padding: 0, // ✅ remove default padding
              minHeight: 40,
              alignItems: 'flex-start',
              '& fieldset': {
                borderColor: alpha(c.primary, 0.2),
              },
              '&:hover fieldset': {
                borderColor: alpha(c.primary, 0.2),
              },
              '&.Mui-error:hover fieldset': {
                borderColor: theme.palette.error.main,
              },
              '&.Mui-focused fieldset': {
                borderColor: alpha(c.primary, 0.2),
                borderWidth: 1.5,
              },
            },

            // 🔹 Input root (chips container)
            '& .MuiAutocomplete-inputRoot': {
              padding: '0 8px !important', // ✅ horizontal padding only
              display: 'flex',
              alignItems: 'center',
              gap: theme.spacing(0.5),
              flexWrap: 'wrap',
              minHeight: 40,
            },

            // The flat padding above replaces MUI's own, which included the
            // right-hand gap it reserves for the clear and dropdown icons.
            // Without that gap a long selection runs underneath them, so it is
            // restored here: one icon's width plus the 8px the adornment sits
            // in from the edge, and both icons' width when the field is
            // clearable.
            '&.MuiAutocomplete-hasPopupIcon .MuiAutocomplete-inputRoot, &.MuiAutocomplete-hasClearIcon .MuiAutocomplete-inputRoot':
              {
                paddingRight: '34px !important',
              },
            '&.MuiAutocomplete-hasPopupIcon.MuiAutocomplete-hasClearIcon .MuiAutocomplete-inputRoot':
              {
                paddingRight: '60px !important',
              },

            // 🔹 Actual text input
            '& .MuiAutocomplete-input': {
              padding: '0 !important',
              margin: 0,
              height: 24,
            },

            // 🔹 End adornments (clear + arrow)
            '& .MuiAutocomplete-endAdornment': {
              top: '50%',
              transform: 'translateY(-50%)',
              right: 8,
              display: 'flex',
              alignItems: 'center',
            },

            // 🔹 Multiple select - full height
            '&[aria-expanded="true"] .MuiOutlinedInput-root, &.MuiAutocomplete-multiple':
              {
                minHeight: 40,
              },
          }),

          // 🔹 Multiple class override
          inputRoot: {
            height: '100%',
            '&.MuiAutocomplete-inputRoot[aria-expanded="false"]': {
              minHeight: 40,
            },
          },

          // 🔹 Chip styling (CENTERED)
          tag: ({ theme }) => ({
            height: 24,
            margin: theme.spacing(0.6),
            display: 'flex',
            alignItems: 'center',
            color: theme.palette.common.black,

            '& .MuiChip-label': {
              padding: '0 8px',
              lineHeight: '24px',
            },

            '& .MuiChip-deleteIcon': {
              margin: 0,
              marginRight: 2,
              fontSize: 16,
            },
          }),

          clearIndicator: {
            padding: 4,
            marginRight: 4,
            borderRadius: '50%',
          },
        },
      },

      MuiTablePagination: {
        styleOverrides: {
          toolbar: {
            '& .MuiIconButton-root': {
              padding: 8,
            },
          },
          select: {
            '&:focus': {
              backgroundColor: 'transparent',
            },
          },
        },
      },
      MuiToolbar: {
        styleOverrides: {
          root: {
            minHeight: '0 !important',
            padding: '0 !important',
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          head: {
            background: alpha(primaryMain, 0.1),
            fontWeight: 600,
          },
          root: {
            transition: 'background-color .2s',

            '&.MuiTableRow-hover:hover': {
              backgroundColor: alpha(c.black, 0.03),
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottomColor: alpha(c.black, 0.08),
            fontSize: 14,
            color: alpha(c.black, 0.8),
            fontWeight: 400,
          },
          head: {
            fontFamily: 'Poppins-Medium',
            textTransform: 'uppercase',
            fontSize: 13,
            fontWeight: 600,
            color: primaryMain,
            backgroundColor: alpha(primaryMain, 0.1),
            borderBottom: `1px solid ${alpha(c.black, 0.08)} !important`,
            height: 48,
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          message: {
            lineHeight: 1.5,
            fontSize: 14,
          },
          standardInfo: {
            color: c.info,
          },
          action: {
            color: alpha(c.black, 0.7),
          },
        },
      },
      MuiStepConnector: {
        styleOverrides: {
          root: {
            top: 9, // vertically aligns the connector line with the icon
            left: 'calc(-50% + 10px)',
            right: 'calc(50% + 10px)',

            '&.Mui-active .MuiStepConnector-line': {
              borderColor: secondaryMain,
            },
            '&.Mui-completed .MuiStepConnector-line': {
              borderColor: secondaryMain,
            },
          },
          line: {
            border: '2px solid ' + alpha(c.primary, 0.1),
          },
        },
      },

      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: textSecondary,
            padding: '8px 16px',
            fontSize: 13,
            opacity: 1,
          },
          arrow: {
            color: alpha(c.black, 0.95),
          },
          popper: {
            offset: [0, -14],
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            width: 35,
            height: 19,
            padding: 0,
            display: 'flex',
            borderRadius: 15,
            boxShadow: 'none',
            '&:active': {
              '& .MuiSwitch-thumb': {
                width: 14,
                boxShadow: 'none',
              },
              '& .MuiSwitch-switchBase.Mui-checked': {
                transform: 'translateX(25px)',
              },
            },
          },
          switchBase: {
            padding: 1,
            '&:hover .MuiSwitch-thumb': {
              boxShadow: 'none', // 👈 no shadow on hover
            },
            // Remove the global ButtonBase disabled background
            '&.Mui-disabled': {
              backgroundColor: 'transparent !important',
            },
            '&.Mui-checked': {
              transform: 'translateX(18px)',
              color: '#fff',
              boxShadow: 'none',
              '& .MuiSwitch-thumb': {
                backgroundColor: 'white', // 👈 FIX: make thumb white when checked
              },
              '& + .MuiSwitch-track': {
                opacity: 1,
                backgroundColor: secondaryMain,
              },
            },
          },
          thumb: {
            width: 13,
            height: 13,
            borderRadius: 10,
            marginBlock: 2,
            marginInline: 1,
            transition: 'width 0.2s ease-in-out',
            background: alpha(secondaryMain, 0.4),
            boxShadow: 'none',
          },
          track: {
            borderRadius: 8,
            opacity: 1,
            backgroundColor: alpha(secondaryMain, 0.2),
            boxSizing: 'border-box',
          },
        },
      },
      MuiStepper: {
        styleOverrides: {
          root: {
            paddingTop: 20,
            paddingBottom: 20,
          },
        },
      },
      MuiStepIcon: {
        styleOverrides: {
          root: {
            borderWidth: 6,
            color: 'white',
            borderRadius: '50%',
            width: 20,
            height: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '6px solid ' + alpha(c.primary, 0.1),
            fontSize: '1rem',
            '&.Mui-active': {
              borderColor: secondaryMain,
              borderWidth: 6,
              color: 'white',
            },
            '&.Mui-completed': {
              border: 'none',
              color: secondaryMain,
            },
          },
          text: {
            '&.Mui-completed .MuiStepIcon-text': {
              display: 'none',
            },
          },
        },
      },

      MuiTypography: {
        defaultProps: {
          variantMapping: {
            h1: 'h1',
            h2: 'h2',
            h3: 'div',
            h4: 'div',
            h5: 'div',
            h6: 'div',
            subtitle1: 'div',
            subtitle2: 'div',
            body1: 'div',
            body2: 'div',
            regCaption: 'div',
            regBody: 'div',
            medBody: 'div',
            medTitle: 'div',
            medCaption: 'div',
          },
        },
        styleOverrides: {
          gutterBottom: {
            marginBottom: 4,
          },
          paragraph: {
            fontSize: 17,
            lineHeight: 1.7,
          },
        },
      },
    },
    sidebar: {
      background: primaryMain,
      textColor: textSecondary,
      dividerBg: s.dividerBg,
      menuItemColor: s.menuItemColor,
      menuItemColorActive: primaryMain,
      menuItemBg: surface,
      menuItemBgActive: s.menuItemBgActive,
      menuItemIconColor: alpha(textSecondary, 0.3),
      menuItemIconColorActive: primaryMain,
      menuItemHeadingColor: alpha(textSecondary, 0.3),
      boxShadow:
        '2px 0 3px rgba(159, 162, 191, .18), 1px 0 1px rgba(159, 162, 191, 0.32)',
      width: 'fit-content',
    },
    header: {
      height: '60px',
      background: c.white,
      boxShadow:
        '0px 2px 3px rgba(159, 162, 191, .18), 0px 1px 1px rgba(159, 162, 191, 0.32)',
      textColor: secondaryMain,
    },
    spacing: 8,
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 960,
        lg: 1280,
        xl: 1840,
      },
    },
    general: {
      reactFrameworkColor: '#00D8FF',
      borderRadiusSm: '6px',
      borderRadius: '10px',
      borderRadiusLg: '12px',
      borderRadiusXl: '16px',
    },
    colors: {
      alpha: {
        white: {
          5: alpha(c.white, 0.02),
          10: alpha(c.white, 0.1),
          30: alpha(c.white, 0.3),
          50: alpha(c.white, 0.5),
          70: alpha(c.white, 0.7),
          100: c.white,
        },
        trueWhite: {
          5: alpha(c.white, 0.02),
          10: alpha(c.white, 0.1),
          30: alpha(c.white, 0.3),
          50: alpha(c.white, 0.5),
          70: alpha(c.white, 0.7),
          100: c.white,
        },
        black: {
          5: alpha(c.black, 0.02),
          10: alpha(c.black, 0.1),
          30: alpha(c.black, 0.3),
          50: alpha(c.black, 0.5),
          70: alpha(c.black, 0.7),
          100: c.black,
        },
      },
      gradients: {
        blue1: g.blue1,
        blue2: g.blue2,
        blue3: g.blue3,
        blue4: g.blue4,
        blue5: g.blue5,
        orange1: g.orange1,
        orange2: g.orange2,
        orange3: g.orange3,
        purple1: g.purple1,
        purple3: g.purple3,
        pink1: g.pink1,
        pink2: g.pink2,
        green1: g.green1,
        green2: g.green2,
        black1: g.black1,
        black2: g.black2,
      },
      shadows: {
        success: alpha(c.success, 0.5),
        error: alpha(c.error, 0.5),
        primary: alpha(c.primary, 0.5),
        warning: alpha(c.warning, 0.5),
        info: alpha(c.info, 0.5),
      },
      primary: {
        lighter: alpha(c.primary, 0.85),
        light: alpha(c.primary, 0.3),
        main: c.primary,
        dark: alpha(c.primary, 0.2),
      },
      secondary: {
        lighter: alpha(c.secondary, 0.85),
        light: alpha(c.secondary, 0.2),
        main: c.secondary,
        dark: alpha(c.secondary, 0.2),
      },
      success: {
        lighter: alpha(c.success, 0.85),
        light: alpha(c.success, 0.3),
        main: c.success,
        dark: alpha(c.success, 0.2),
      },
      warning: {
        lighter: alpha(c.warning, 0.85),
        light: alpha(c.warning, 0.3),
        main: c.warning,
        dark: alpha(c.warning, 0.2),
      },
      error: {
        lighter: alpha(c.error, 0.85),
        light: alpha(c.error, 0.3),
        main: c.error,
        dark: alpha(c.error, 0.2),
      },
      info: {
        lighter: alpha(c.info, 0.85),
        light: alpha(c.info, 0.3),
        main: c.info,
        dark: alpha(c.info, 0.2),
      },
    },
  });

  return theme;
}
